import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Eye, EyeOff, GitMerge, Link2, Unlink, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { RUN_TRIGGER_LABEL, useAssignees } from '../lib/automation';
import { useAuth } from '../lib/auth';
import { useDesk } from '../lib/desk';
import { formatDate, formatRelative } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import type { AutomationRun, Page, RelatedTicket, RelatedTickets, WorkOrder, WorkOrderDetail } from '../lib/types';
import { RunStatusBadge } from './automation-runs';
import { useToast } from './toast';
import { Badge, Button, Card, CardHeader, ConfirmDialog, Input, Select } from './ui';
import { WorkOrderStatusBadge } from './work-order-badges';

type MergePlan = { source: { id: string; number: number }; target: { id: string; number: number } };

function RelatedRow({ ticket, actions }: { ticket: RelatedTicket; actions: React.ReactNode }) {
  const desk = useDesk();
  return (
    <li className="flex flex-wrap items-start gap-3 px-5 py-3">
      <div className="min-w-0 flex-1">
        <Link to={desk.workOrderHref(ticket.id)} className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900 hover:text-brand-700">
          <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
          <WorkOrderStatusBadge status={ticket.status} />
        </Link>
        <p className="mt-0.5 text-xs text-slate-500">
          {ticket.requesterName} · {ticket.resolvedAt ? `resolved ${formatDate(ticket.resolvedAt)}` : `opened ${formatRelative(ticket.createdAt)}`}
          {ticket.assignedTo && ` · ${ticket.assignedTo.name}`}
        </p>
        <div className="mt-1 flex flex-wrap gap-1">
          {ticket.reasons.map((reason) => (
            <Badge key={reason} tone="amber">
              {reason}
            </Badge>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-1">{actions}</div>
    </li>
  );
}

/** Possible duplicates, repeat issues, and linked tickets, with link and merge. */
export function TicketRelationsCard({ ticket }: { ticket: WorkOrderDetail }) {
  const navigate = useNavigate();
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canLink = ticket.permissions.link;
  const [merge, setMerge] = useState<MergePlan | null>(null);
  const [linkNumber, setLinkNumber] = useState('');
  const related = useQuery({
    queryKey: ['work-order-related', ticket.id],
    queryFn: () => api.get<RelatedTickets>(`/work-orders/${ticket.id}/related`),
    enabled: !ticket.mergedIntoId,
  });

  const refresh = () => invalidateInventory(queryClient);
  const link = useMutation({
    mutationFn: (targetId: string) => api.post(`/work-orders/${ticket.id}/links`, { targetId }),
    onSuccess: () => {
      refresh();
      setLinkNumber('');
      toast.success('Tickets linked');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const unlink = useMutation({
    mutationFn: (linkId: string) => api.delete(`/work-orders/${ticket.id}/links/${linkId}`),
    onSuccess: () => refresh(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const doMerge = useMutation({
    mutationFn: (plan: MergePlan) => api.post<WorkOrderDetail>(`/work-orders/${plan.source.id}/merge`, { targetId: plan.target.id }),
    onSuccess: (target, plan) => {
      refresh();
      setMerge(null);
      toast.success(`Merged #${plan.source.number} into #${plan.target.number}`);
      if (target.id !== ticket.id) navigate(desk.workOrderHref(target.id));
    },
    onError: (err) => {
      setMerge(null);
      toast.error(errorMessage(err));
    },
  });

  const linkByNumber = async (event: FormEvent) => {
    event.preventDefault();
    const number = Number(linkNumber.replace(/^#/, ''));
    if (!Number.isInteger(number) || number <= 0) return;
    try {
      const found = await api.get<Page<WorkOrder>>('/work-orders', { service: ticket.service, state: 'all', search: String(number), pageSize: 5 });
      const target = found.items.find((t) => t.number === number);
      if (!target) toast.error(`There’s no ticket #${number}`);
      else link.mutate(target.id);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const self = { id: ticket.id, number: ticket.number };
  const duplicates = related.data?.duplicates ?? [];
  const repeats = related.data?.repeats ?? [];
  const nothing = !related.isLoading && duplicates.length === 0 && repeats.length === 0 && ticket.links.length === 0;

  return (
    <Card>
      <CardHeader title="Related tickets" description="Possible duplicates and repeat issues, so a problem gets worked once." />
      {related.isLoading && <p className="px-5 py-4 text-sm text-slate-500">Looking for similar tickets…</p>}

      {duplicates.length > 0 && (
        <section>
          <p className="border-b border-slate-100 bg-amber-50/60 px-5 py-2 text-xs font-semibold tracking-wide text-amber-900 uppercase">Possible duplicates</p>
          <ul className="divide-y divide-slate-100">
            {duplicates.map((other) => (
              <RelatedRow
                key={other.id}
                ticket={other}
                actions={
                  canLink && (
                    <>
                      <Button size="sm" variant="ghost" icon={<Link2 />} onClick={() => link.mutate(other.id)}>
                        Link
                      </Button>
                      <Button size="sm" icon={<GitMerge />} onClick={() => setMerge({ source: { id: other.id, number: other.number }, target: self })}>
                        Merge into this
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setMerge({ source: self, target: { id: other.id, number: other.number } })}>
                        Merge this into #{other.number}
                      </Button>
                    </>
                  )
                }
              />
            ))}
          </ul>
        </section>
      )}

      {repeats.length > 0 && (
        <section>
          <p className="border-y border-slate-100 bg-slate-50 px-5 py-2 text-xs font-semibold tracking-wide text-slate-600 uppercase">
            Happened before ({repeats.length} in the last 6 months)
          </p>
          <ul className="divide-y divide-slate-100">
            {repeats.map((other) => (
              <RelatedRow
                key={other.id}
                ticket={other}
                actions={
                  canLink && (
                    <Button size="sm" variant="ghost" icon={<Link2 />} onClick={() => link.mutate(other.id)}>
                      Link
                    </Button>
                  )
                }
              />
            ))}
          </ul>
        </section>
      )}

      {ticket.links.length > 0 && (
        <section>
          <p className="border-y border-slate-100 bg-slate-50 px-5 py-2 text-xs font-semibold tracking-wide text-slate-600 uppercase">Linked</p>
          <ul className="divide-y divide-slate-100">
            {ticket.links.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                <Badge tone={item.type === 'DUPLICATE' ? 'violet' : 'gray'}>
                  {item.type === 'DUPLICATE' ? (item.direction === 'incoming' ? 'Merged in' : 'Merged into') : 'Related'}
                </Badge>
                <Link to={desk.workOrderHref(item.ticket.id)} className="min-w-0 flex-1 truncate text-slate-800 hover:text-brand-700">
                  <span className="text-slate-400">#{item.ticket.number}</span> {item.ticket.title}
                </Link>
                <WorkOrderStatusBadge status={item.ticket.status} />
                {canLink && item.type === 'RELATED' && (
                  <button type="button" onClick={() => unlink.mutate(item.id)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600" aria-label="Unlink">
                    <Unlink className="size-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {nothing && <p className="px-5 py-4 text-sm text-slate-500">No similar open tickets, and nothing like it before.</p>}

      {canLink && (
        <form onSubmit={linkByNumber} className="flex items-center gap-2 border-t border-slate-100 px-5 py-3">
          <Input value={linkNumber} onChange={(e) => setLinkNumber(e.target.value)} placeholder="Ticket #" className="w-32" aria-label="Ticket number to link" />
          <Button type="submit" size="sm" icon={<Link2 />} loading={link.isPending} disabled={!linkNumber.trim()}>
            Link a ticket
          </Button>
        </form>
      )}

      <ConfirmDialog
        open={!!merge}
        onClose={() => setMerge(null)}
        onConfirm={() => merge && doMerge.mutate(merge)}
        loading={doMerge.isPending}
        tone="primary"
        title={merge ? `Merge #${merge.source.number} into #${merge.target.number}?` : ''}
        description={
          merge &&
          `#${merge.source.number} will be closed as a duplicate. Its details are added to #${merge.target.number} as an internal note, and its watchers follow #${merge.target.number}.`
        }
        confirmLabel="Merge"
      />
    </Card>
  );
}

export function WatchersCard({ ticket }: { ticket: WorkOrderDetail }) {
  const { me, can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const assignees = useAssignees(can('TECHNICIAN'));
  const watching = ticket.watchers.some((w) => w.id === me?.user.id);

  const change = useMutation({
    mutationFn: ({ userId, watch }: { userId: string; watch: boolean }) =>
      watch ? api.put(`/work-orders/${ticket.id}/watchers/${userId}`) : api.delete(`/work-orders/${ticket.id}/watchers/${userId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-order', ticket.id] }),
    onError: (err) => toast.error(errorMessage(err)),
  });

  const addable = (assignees.data ?? []).filter((user) => !ticket.watchers.some((w) => w.id === user.id));

  return (
    <Card>
      <CardHeader
        title="Watchers"
        description="Included when workflows notify watchers"
        actions={
          me && (
            <Button size="sm" variant="ghost" icon={watching ? <EyeOff /> : <Eye />} onClick={() => change.mutate({ userId: me.user.id, watch: !watching })}>
              {watching ? 'Stop watching' : 'Watch'}
            </Button>
          )
        }
      />
      <div className="space-y-3 p-5">
        {ticket.watchers.length === 0 ? (
          <p className="text-sm text-slate-500">Nobody is watching this ticket.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {ticket.watchers.map((watcher) => (
              <span key={watcher.id} className="inline-flex items-center gap-1 rounded-md bg-slate-100 py-0.5 pr-1 pl-2 text-xs font-medium text-slate-700">
                {watcher.name}
                {(can('TECHNICIAN') || watcher.id === me?.user.id) && (
                  <button type="button" onClick={() => change.mutate({ userId: watcher.id, watch: false })} className="rounded p-0.5 hover:bg-white" aria-label={`Remove ${watcher.name}`}>
                    <X className="size-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        {can('TECHNICIAN') && addable.length > 0 && (
          <Select value="" onChange={(e) => e.target.value && change.mutate({ userId: e.target.value, watch: true })} aria-label="Add a watcher">
            <option value="">Add a watcher…</option>
            {addable.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </Select>
        )}
      </div>
    </Card>
  );
}

/** What workflows, smart rules, and schedules did on this ticket. */
export function TicketAutomationCard({ ticketId }: { ticketId: string }) {
  const runs = useQuery({
    queryKey: ['work-order-automation', ticketId],
    queryFn: () => api.get<AutomationRun[]>(`/work-orders/${ticketId}/automation`),
  });
  // Skipped runs are noise here; the run history keeps them.
  const shown = (runs.data ?? []).filter((run) => run.status !== 'SKIPPED');
  if (runs.isLoading || shown.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Automation"
        actions={
          <Link to="/automation?tab=runs" className="text-xs font-medium text-brand-700 hover:underline">
            Run history
          </Link>
        }
      />
      <ul className="divide-y divide-slate-100">
        {shown.slice(0, 8).map((run) => (
          <li key={run.id} className="px-5 py-2.5 text-sm">
            <div className="flex items-center gap-2">
              <Bot className="size-4 shrink-0 text-slate-400" />
              {run.workflowId ? (
                <Link to={`/automation/workflows/${run.workflowId}`} className="min-w-0 flex-1 truncate font-medium text-slate-800 hover:text-brand-700">
                  {run.name}
                </Link>
              ) : (
                <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{run.name}</span>
              )}
              <RunStatusBadge status={run.status} />
            </div>
            <p className="mt-0.5 pl-6 text-xs text-slate-500">
              {RUN_TRIGGER_LABEL[run.trigger] ?? run.trigger} · {formatRelative(run.startedAt)}
              {run.message && ` · ${run.message}`}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
